import React, { useRef } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Checkbox as AntCheckbox } from 'antd';
import { RightOutlined } from '@ant-design/icons';

import { SubDropdown } from './SubDropdown';
import { getFilterItemId } from './filterItemIds';
import { formatMenuLabelText, isMenuLabelTruncated } from './menuLabelDisplay';
import styles from './OrganizationDropdown.module.css';

export const DropdownItem = ({
  parent,
  fieldName,
  checkboxClassName,
  checked,
  indeterminate,
  hasChildren,
  showArrow,
  isSubOpen,
  onParentCheckbox,
  onChildCheckbox,
  selectedIds,
  onActivate,
  onScheduleClose,
  onCancelClose,
  onClearSub,
}) => {
  const parentId = getFilterItemId(parent);
  const rowRef = useRef(null);
  const parentWrapRef = useRef(null);
  const parentTitle = parent?.title ?? '';
  const parentLabel = formatMenuLabelText(parentTitle);

  const handleRowClick = (e) => {
    if (!hasChildren || !showArrow) return;
    if (e.target.closest('label') || e.target.closest('input')) return;
    onActivate(parentId);
  };

  return (
    <div
      ref={parentWrapRef}
      className={classnames(styles.parentWrap, { [styles.parentWrapSubOpen]: isSubOpen && hasChildren })}
      onMouseEnter={() => {
        onCancelClose();
        if (hasChildren && showArrow) onActivate(parentId);
        else onClearSub();
      }}
      onMouseLeave={() => {
        if (hasChildren && showArrow) onScheduleClose();
      }}>
      <div
        role={hasChildren && showArrow ? 'button' : undefined}
        tabIndex={hasChildren && showArrow ? 0 : undefined}
        ref={rowRef}
        className={classnames(styles.itemRow, {
          [styles.itemRowActive]: isSubOpen && hasChildren,
          [styles.itemRowChecked]: checked || (hasChildren && indeterminate),
        })}
        onClick={handleRowClick}
        onKeyDown={(e) => {
          if (!hasChildren || !showArrow) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onActivate(parentId);
          }
        }}>
        <label className={styles.checkboxLabelWrap}>
          <AntCheckbox
            name={`${fieldName}__${parentId}`}
            className={checkboxClassName}
            checked={checked}
            indeterminate={Boolean(hasChildren && indeterminate)}
            skipGroup
            onChange={(e) => onParentCheckbox({ checked: e.target.checked })}
            onClick={(e) => e.stopPropagation()}
          />
          <span
            className={styles.itemLabel}
            title={isMenuLabelTruncated(parentTitle) ? parentTitle : undefined}>
            {parentLabel}
          </span>
        </label>
        {showArrow ? (
          <div className={styles.arrowCell}>
            <RightOutlined
              className={classnames(styles.rowChevron, {
                [styles.rowChevronInteractive]: hasChildren,
              })}
              onClick={(e) => {
                e.stopPropagation();
                if (hasChildren) onActivate(parentId);
              }}
            />
          </div>
        ) : (
          <div className={styles.arrowCell} aria-hidden />
        )}
      </div>

      {hasChildren && showArrow && isSubOpen ? (
        <>
          <div className={styles.subBridge} aria-hidden />
          <SubDropdown
            anchorRef={rowRef}
            portalContainerRef={parentWrapRef}
            onMouseEnter={onCancelClose}
            onMouseLeave={onScheduleClose}>
            {parent.children.map((child) => {
              const childChecked = selectedIds.has(getFilterItemId(child));
              const childTitle = child?.title ?? '';
              const childLabel = formatMenuLabelText(childTitle);
              return (
                <label key={getFilterItemId(child)} className={styles.subRow}>
                  <AntCheckbox
                    name={`${fieldName}__${getFilterItemId(child)}`}
                    className={checkboxClassName}
                    checked={childChecked}
                    skipGroup
                    onChange={(e) => onChildCheckbox(parent, child)({ checked: e.target.checked })}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span
                    className={styles.subLabel}
                    title={isMenuLabelTruncated(childTitle) ? childTitle : undefined}>
                    {childLabel}
                  </span>
                </label>
              );
            })}
          </SubDropdown>
        </>
      ) : null}
    </div>
  );
};

DropdownItem.propTypes = {
  parent: PropTypes.object.isRequired,
  fieldName: PropTypes.string.isRequired,
  checkboxClassName: PropTypes.string,
  checked: PropTypes.bool.isRequired,
  indeterminate: PropTypes.bool.isRequired,
  hasChildren: PropTypes.bool.isRequired,
  showArrow: PropTypes.bool.isRequired,
  isSubOpen: PropTypes.bool.isRequired,
  onParentCheckbox: PropTypes.func.isRequired,
  onChildCheckbox: PropTypes.func.isRequired,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
  onActivate: PropTypes.func.isRequired,
  onScheduleClose: PropTypes.func.isRequired,
  onCancelClose: PropTypes.func.isRequired,
  onClearSub: PropTypes.func.isRequired,
};
